import React, { FC } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation, TFunction } from 'react-i18next';
import { CustomTableHeadersProps, TertiaryButton, SimplePopover, FilterIcon, Tag, palette } from '@dev/ee-components';
import { AccessLevel, JourneyStatus } from '@dev/ee-api/ee-api-models';
import { filter, isEmpty } from 'lodash-es';

import { DeleteAutomation, getAutomationStore, GetAllAutomationsStart } from '@/store/automations/actions';
import { getAccountAccessLevel } from '@/store/account/actions';
import { ShowDialog } from '@/store/dialog/actions';
import { ShowDialogParams } from '@/store/dialog/types';
import { AutomationsInterface } from '@/store/automations/types';
import { GetAutomationsParams } from '@/services/ElasticApi';
import { calculateOffset } from '@/services/tools/handleUrlParams';
import { DEFAULT_OFFSET } from '@/services/constants';
import { CustomPopoverBodyProps } from '../../AutomationListInterface';

const FilterContainer = styled.div`
    width: 100%;
    display: inline-flex;
    justify-content: space-between;
    align-items: center;
`;
const FilterLabel = styled.span`
    margin: 0 1em;
    display: flex;
    align-items: center;
`;

type FilterOptions = { label: string; value: string; count: number };

const getStatusPopoverOptions = (automations: AutomationsInterface, t: TFunction): FilterOptions[] => {
    const { statusCounts } = automations;

    const possibleStatus = [
        {
            label: t('active'),
            value: JourneyStatus.Active,
            count: statusCounts?.active,
        },
        {
            label: t('paused'),
            value: JourneyStatus.Paused,
            count: statusCounts?.paused,
        },
        {
            label: t('invalid'),
            value: JourneyStatus.Invalid,
            count: statusCounts?.invalid,
        },
    ];

    return filter(possibleStatus, (status): boolean => status.count > 0);
};

const CustomPopoverBody: FC<CustomPopoverBodyProps> = (props: CustomPopoverBodyProps): JSX.Element => (
    <FilterContainer>
        <FilterLabel>{props.element.label}</FilterLabel>
        {props.element.count && <Tag label={props.element.count} clickable={false} />}
    </FilterContainer>
);

const StatusFilters: FC<{ automations: AutomationsInterface }> = ({
    automations,
}: {
    automations: AutomationsInterface;
}): JSX.Element => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const options = getStatusPopoverOptions(automations, t);
    const { pagination } = automations;

    const requestParams: GetAutomationsParams = {
        from: pagination?.from,
        to: pagination?.to,
        statuses: pagination?.statuses,
        name: pagination?.name,
        limit: pagination?.perPage,
        offset: calculateOffset(pagination?.page, pagination?.perPage),
    };

    if (options?.length <= 1) {
        return null;
    }

    return (
        <SimplePopover
            type="icon"
            options={options}
            defaultCheckboxValue={[]}
            enableCheckbox={true}
            onCheckboxClick={(ev, selectedItems: []) => {
                const statuses = isEmpty(selectedItems)
                    ? [JourneyStatus.Invalid, JourneyStatus.Active, JourneyStatus.Paused]
                    : selectedItems;
                dispatch(GetAllAutomationsStart({ ...requestParams, statuses, offset: DEFAULT_OFFSET }));
            }}
            popperProps={{
                disablePortal: false,
            }}
            // eslint-disable-next-line react/no-unstable-nested-components
            customDropdownBody={(element: any): JSX.Element => <CustomPopoverBody element={element} />}
        >
            <FilterIcon />
        </SimplePopover>
    );
};

export const useTableHeaders = (): CustomTableHeadersProps[] => {
    const { t } = useTranslation();
    const automations = useSelector(getAutomationStore);
    const accountAl = useSelector(getAccountAccessLevel);
    const dispatch = useDispatch();
    const { checkedAutomations } = automations;
    const checkedAutomationsLength = checkedAutomations.length;

    const dialogParams: ShowDialogParams = {
        title: `${t('delete')} ${checkedAutomationsLength} ${
            checkedAutomationsLength > 1 ? t('automations').toLowerCase() : t('automation').toLowerCase()
        }`,
        destructiveLabel: t('delete'),
        onClickDestructive: (): void => {
            dispatch(DeleteAutomation(checkedAutomations));
        },
        cancelLabel: t('cancel'),
        body: t('deleteautomationsmessage'),
    };

    if (checkedAutomationsLength) {
        return [
            {
                id: 'automationName',
                headerAlign: 'left',
                columnAlign: 'left',
                label: t('name'),
            },
            {
                id: 'opened',
                headerAlign: 'right',
                label: '',
                columnAlign: 'right',
            },
            {
                id: 'clicked',
                headerAlign: 'right',
                label: '',
                columnAlign: 'right',
            },
            {
                id: 'status',
                headerAlign: 'center',
                label: '',
                columnAlign: 'center',
            },
            {
                id: 'viewreport',
                label: '',
                headerAlign: 'right',
                columnAlign: 'center',
                filterIcon: accountAl?.includes(AccessLevel.ModifyAutomations) && (
                    <TertiaryButton
                        style={{ margin: 0, color: palette.red[6] }}
                        text={t('delete')}
                        onClick={() => {
                            dispatch(ShowDialog(dialogParams));
                        }}
                    />
                ),
            },
        ];
    }
    return [
        {
            id: 'automationName',
            headerAlign: 'left',
            columnAlign: 'left',
            label: t('name'),
        },
        {
            id: 'opened',
            headerAlign: 'right',
            label: t('opened'),
            columnAlign: 'right',
        },
        {
            id: 'clicked',
            headerAlign: 'right',
            label: t('clicked'),
            columnAlign: 'right',
        },
        {
            id: 'status',
            headerAlign: 'center',
            label: t('status'),
            columnAlign: 'center',
            filterIcon: <StatusFilters automations={automations} />,
        },
        {
            id: 'viewreport',
            headerAlign: 'center',
            label: '',
            columnAlign: 'center',
        },
    ];
};
